import { Injectable } from '@angular/core';
import {
    HttpErrorResponse,
    HttpEvent,
    HttpHandler,
    HttpInterceptor,
    HttpRequest,
    HttpResponse,
} from '@angular/common/http';
import { filter, Observable, of, tap, throwError } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { ApolloError } from '@apollo/client/core';
import {
    AppSnackbarService,
    SnackBarMessageTypeEnum,
} from './shared/app-snackbar/app-snackbar.service';
import { Router } from '@angular/router';

type ValidationErrorGraphql = {
    field: string;
    message: string;
    validation: string;
};

type ErrorGraphql = {
    message: string;
    extensions: {
        code?: string;
        httpCode?: number;
        exception?: string;
        validation?: Array<ValidationErrorGraphql>;
    };
};

type ResponseErrorGraphql = {
    errors: ErrorGraphql[];
};

@Injectable()
export class ApolloInterceptor implements HttpInterceptor {
    constructor(
        private _snackBar: AppSnackbarService,
        private _router: Router
    ) {}

    intercept(
        req: HttpRequest<any>,
        next: HttpHandler
    ): Observable<HttpEvent<any>> {
        return next.handle(req).pipe(
            filter((event) => event instanceof HttpResponse),
            tap((event: HttpResponse<any>) => {
                this.handleErrorOrSuccess(event);
            }),
            catchError((error: HttpErrorResponse | ApolloError) => {
                const handled = this.handleErrorOrSuccess(error);
                if (handled) {
                    return of(null); // erro previsto → fluxo continua
                }
                return throwError(() => error); // erro inesperado → deixa quebrar
            })
        );
    }

    /**
     * Função unificada que trata erros e mensagens de sucesso
     * para HttpResponse e ApolloError.
     * Retorna true se o erro foi tratado, false caso contrário.
     */
    private handleErrorOrSuccess(
        source: HttpResponse<any> | ApolloError | HttpErrorResponse
    ): boolean {
        // HttpResponse normal
        if (source instanceof HttpResponse) {
            const isError =
                source.status === 200 && source.body?.errors?.length > 0;
            const isSuccess = source.status === 200 && source.body?.data;

            if (isError) {
                return this.processGraphQLErrors(source.body.errors);
            } else if (isSuccess) {
                this.showSuccessMessages(source.body.data);
            }
            return false;
        }

        // ApolloError
        if (source instanceof ApolloError) {
            if (source.graphQLErrors?.length) {
                return this.processGraphQLErrors(source.graphQLErrors);
            }
            return false;
        }

        // HttpErrorResponse puro (rede, 500, etc.)
        if (source instanceof HttpErrorResponse) {
            this._snackBar.openSnackBar(
                `Falha ao se comunicar com servidor: [${
                    source.name || 'Erro'
                }] ${source.message || ''}`,
                SnackBarMessageTypeEnum.error
            );
            return true;
        }

        // Fallback genérico
        this._snackBar.openSnackBar(
            `Falha ao se comunicar com servidor.`,
            SnackBarMessageTypeEnum.error
        );
        return true;
    }

    /**
     * Processa lista de erros GraphQL.
     * Retorna true se algum erro foi tratado.
     */
    private processGraphQLErrors(errors: readonly any[]): boolean {
        const handledCodes = ['CNPJ_INVALID'];
        const messagePatterns = [
            'Unable to fit value into a 64-bit signed integer',
        ];
        const noPermissionCodes = ['e_unauthorized', 'acl_not_enough_permis'];

        let handled = false;

        errors.forEach((err) => {
            if (
                err.extensions?.code === 'e_unauthorized' &&
                err.extensions?.httpCode === 401
            ) {
                this._snackBar.openSnackBar(
                    'Faça login para continuar.',
                    SnackBarMessageTypeEnum.error,
                    7000
                );
                this._router.navigate(['/sair']);
                handled = true;
            } else if (
                noPermissionCodes.includes(err.extensions?.code) &&
                (err.extensions?.httpCode === 429 ||
                    err.extensions?.httpCode === 403)
            ) {
                this._snackBar.openSnackBar(
                    'Você não tem autorização para acessar isto.',
                    SnackBarMessageTypeEnum.error,
                    7000
                );
                this._router.navigate(['/404-not-found']);
                handled = true;
            } else if (err.extensions?.validation?.length) {
                const fields: string[] = err.extensions.validation.map(
                    (v) => `[${v.field}] ${v.message}.`
                );
                this._snackBar.openSnackBarMultiLine(
                    err.message,
                    fields,
                    SnackBarMessageTypeEnum.error
                );
                handled = true;
            } else if (
                handledCodes.includes(err.extensions?.code) &&
                err.extensions?.httpCode === 400
            ) {
                this._snackBar.openSnackBar(
                    err.message || 'Erro ao consultar.',
                    SnackBarMessageTypeEnum.error,
                    7000
                );
                handled = true;
            } else if (
                messagePatterns.some((pattern) =>
                    err.message?.includes(pattern)
                )
            ) {
                this._snackBar.openSnackBar(
                    'O código informado é inválido ou muito longo.',
                    SnackBarMessageTypeEnum.error,
                    7000
                );
                handled = true;
            } else if (err.message) {
                this._snackBar.openSnackBar(
                    err.message,
                    SnackBarMessageTypeEnum.error
                );
                handled = true;
            }
        });

        return handled;
    }

    /**
     * Mostra mensagens de sucesso se existirem
     */
    private showSuccessMessages(data: any) {
        for (const key in data) {
            if (data.hasOwnProperty(key)) {
                const element = data[key];
                if (element?.message) {
                    this._snackBar.openSnackBar(
                        element.message,
                        SnackBarMessageTypeEnum.success
                    );
                }
            }
        }
    }
}
